import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';

/**
 * Interface representing the data associated with each leaf
 * @interface LeafData
 * @property {number} speed - The vertical falling speed of the leaf
 * @property {number} wobbleSpeed - The speed of the horizontal wobble movement
 * @property {number} wobbleRange - The maximum horizontal distance the leaf can wobble
 * @property {number} rotationSpeed - The speed of rotation of the leaf
 */
interface LeafData {
  speed: number;
  wobbleSpeed: number;
  wobbleRange: number;
  rotationSpeed: number;
}

/**
 * A class that creates and manages an animated leaf falling effect
 * @extends View
 *
 * @example
 * const leafEffect = new LeafEffect({
 *   superview: parentView,
 *   width: 800,
 *   height: 600
 * });
 *
 * // Start the leaf effect
 * leafEffect.start();
 *
 * // Later, stop the effect
 * leafEffect.stop();
 */
export class LeafEffect extends View {
  /** Number of leaves to create */
  private static readonly NUM_LEAVES = 50;
  /** Minimum scale factor for leaves */
  private static readonly MIN_SCALE = 0.5;
  /** Range of random scale variation */
  private static readonly SCALE_RANGE = 0.7;
  /** Base speed for leaf falling motion */
  private static readonly BASE_SPEED = 0.02;
  /** Base speed for leaf wobble motion */
  private static readonly WOBBLE_BASE = 0.001;
  /** Maximum range of horizontal wobble motion */
  private static readonly WOBBLE_RANGE = 40;
  /** Base rotation speed for leaves */
  private static readonly ROTATION_BASE = 0.0001;
  /** Chance of slowing down vertical speed */
  private static readonly FLOAT_CHANCE = 0.03;

  /** Array of leaf view instances */
  private readonly leaves: ImageScaleView[] = [];
  /** Array of data objects containing movement parameters for each leaf */
  private readonly leafData: LeafData[] = [];

  /** ID of the current animation frame request */
  private animationFrameId: number | null = null;
  /** Timestamp of the last animation update */
  private lastTimestamp = 0;
  /** Flag indicating whether the leaf effect is currently active */
  private isActive = false;

  /**
   * Creates a new LeafEffect instance
   * @param {ViewOpts} opts - Configuration options for the view
   *
   * @example
   * const leafEffect = new LeafEffect({
   *   superview: parentView,
   *   width: 800,
   *   height: 600
   * });
   */
  constructor(opts: ViewOpts) {
    super({ ...opts, visible: false });
    this.initializeLeaves();
  }

  /**
   * Initializes the leaves for the leaf effect
   *
   * Creates a set of leaf instances based on NUM_LEAVES constant.
   * Each leaf is given random properties within defined ranges for:
   * - Scale (affects size)
   * - Position (within view bounds)
   * - Movement parameters (speed, wobble)
   *
   * @private
   */
  private initializeLeaves(): void {
    const { width, height } = this.style;

    for (let i = 0; i < LeafEffect.NUM_LEAVES; i++) {
      const scale =
        Math.random() * LeafEffect.SCALE_RANGE + LeafEffect.MIN_SCALE;

      this.leaves.push(
        new ImageScaleView({
          superview: this,
          image: 'assets/ui/match2/common/particle_leaf.png',
          width: 32,
          height: 32,
          scale,
          x: Math.random() * width,
          y: Math.random() * height,
          centerOnOrigin: true,
        }),
      );

      this.leafData.push({
        speed: scale * LeafEffect.BASE_SPEED,
        wobbleSpeed: LeafEffect.WOBBLE_BASE * scale,
        wobbleRange: LeafEffect.WOBBLE_RANGE,
        rotationSpeed: LeafEffect.ROTATION_BASE * (Math.random() * 2 - 1), // Random direction
      });
    }
  }

  /**
   * Starts the leaf falling effect animation
   *
   * Makes the effect visible with a fade-in animation and initiates
   * the animation loop using requestAnimationFrame.
   * Does nothing if the effect is already active.
   *
   * @public
   *
   * @example
   * leafEffect.start();
   */
  public start(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.updateOpts({ visible: true, opacity: 0 });
    animate(this).now({ opacity: 1 }, 500);

    this.lastTimestamp = performance.now();
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }

  /**
   * Stops the leaf falling effect animation
   *
   * Fades out the effect and stops the animation loop.
   * Sets the view to invisible after fade-out completes.
   * Does nothing if the effect is already inactive.
   *
   * @public
   *
   * @example
   * leafEffect.stop();
   */
  public stop(): void {
    if (!this.isActive) return;

    this.isActive = false;
    animate(this)
      .now({ opacity: 0 }, 500)
      .then(() => this.updateOpts({ visible: false }));

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Handles the animation frame updates
   *
   * Called by requestAnimationFrame to update leaf positions
   * and schedule the next frame if the effect is still active.
   *
   * @param {number} timestamp - The current timestamp from requestAnimationFrame
   * @private
   */
  private animate(timestamp: number): void {
    if (!this.isActive) return;

    this.updateLeaves(timestamp);
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }

  /**
   * Updates the position of all leaves
   *
   * Calculates new positions based on:
   * - Time elapsed since last update (for consistent speed)
   * - Individual leaf parameters (speed, wobble)
   * - View boundaries (for wrapping)
   *
   * @param {number} timestamp - The current animation timestamp
   * @private
   */
  private updateLeaves(timestamp: number): void {
    const dt = this.lastTimestamp ? timestamp - this.lastTimestamp : 0;
    this.lastTimestamp = timestamp;

    const { width, height } = this.style;

    for (let i = 0; i < this.leaves.length; i++) {
      const leaf = this.leaves[i];
      const data = this.leafData[i];

      // Check for entering floating
      if (Math.random() < LeafEffect.FLOAT_CHANCE) {
        // Slow down gradually
        data.speed = Math.max(data.speed * 0.3, LeafEffect.BASE_SPEED * 0.2);
      } else if (data.speed < LeafEffect.BASE_SPEED) {
        // Gradually return to normal speed
        data.speed = Math.min(LeafEffect.BASE_SPEED, data.speed * 1.1);
      }

      // Update horizontal position (wobble)
      leaf.style.offsetX =
        Math.sin(timestamp * data.wobbleSpeed) * data.wobbleRange;

      // Update rotation
      leaf.style.r = (leaf.style.r || 0) + dt * data.rotationSpeed * 30;

      // Update vertical position
      leaf.style.y += dt * data.speed;

      // Reset position if leaf is below screen
      if (leaf.style.y > height + leaf.style.height) {
        leaf.style.x = Math.random() * width;
        leaf.style.y = -leaf.style.height;
      }
    }
  }
}
